export const TIMED_TASK_STATUS = {
  PENDING: 0,
  RUNNING: 1,
  COMPLETED: 2,
  RECALLED: 3,
  FAILED: 4,
  DRAFT: 5,
  CANCELLED: 6,
} as const;

export type TimedTaskStatus =
  (typeof TIMED_TASK_STATUS)[keyof typeof TIMED_TASK_STATUS];

export interface TimedTaskStatusConfig {
  label: string;
  tagColor: string;
  textClassName: string;
  resultBgClassName: string;
  resultTitle: string;
  editable: boolean;
}

export const TIMED_TASK_STATUS_CONFIG: Record<TimedTaskStatus, TimedTaskStatusConfig> =
  {
    [TIMED_TASK_STATUS.PENDING]: {
      label: '待执行',
      tagColor: 'blue',
      textClassName: 'text-[#1890ff]',
      resultBgClassName: 'bg-[#f0f9ff]',
      resultTitle: '✅ 定时任务已创建成功！',
      editable: true,
    },
    [TIMED_TASK_STATUS.RUNNING]: {
      label: '执行中',
      tagColor: 'processing',
      textClassName: 'text-[#1890ff]',
      resultBgClassName: 'bg-[#f0f9ff]',
      resultTitle: '✅ 定时任务已创建成功！',
      editable: false,
    },
    [TIMED_TASK_STATUS.COMPLETED]: {
      label: '已完成',
      tagColor: 'success',
      textClassName: 'text-[#52c41a]',
      resultBgClassName: 'bg-[#f0f9ff]',
      resultTitle: '✅ 定时任务已创建成功！',
      editable: false,
    },
    [TIMED_TASK_STATUS.RECALLED]: {
      label: '已撤回',
      tagColor: 'default',
      textClassName: 'text-[#fa8c16]',
      resultBgClassName: 'bg-[#fff7e6]',
      resultTitle: '⚠️ 定时任务已撤回',
      editable: false,
    },
    [TIMED_TASK_STATUS.FAILED]: {
      label: '执行失败',
      tagColor: 'error',
      textClassName: 'text-[#ff4d4f]',
      resultBgClassName: 'bg-[#f0f9ff]',
      resultTitle: '✅ 定时任务已创建成功！',
      editable: false,
    },
    [TIMED_TASK_STATUS.DRAFT]: {
      label: '草稿',
      tagColor: 'purple',
      textClassName: 'text-[#722ed1]',
      resultBgClassName: 'bg-[#f9f0ff]',
      resultTitle: '✅ 草稿已保存',
      editable: true,
    },
    [TIMED_TASK_STATUS.CANCELLED]: {
      label: '取消',
      tagColor: 'default',
      textClassName: 'text-[#fa8c16]',
      resultBgClassName: 'bg-[#fff7e6]',
      resultTitle: '⚠️ 定时任务已取消',
      editable: false,
    },
  };

export const DEFAULT_TIMED_TASK_STATUS_CONFIG: TimedTaskStatusConfig = {
  label: '未知',
  tagColor: 'default',
  textClassName: 'text-[#1890ff]',
  resultBgClassName: 'bg-[#f0f9ff]',
  resultTitle: '✅ 定时任务已创建成功！',
  editable: false,
};

export const TIMED_TASK_STATUS_OPTIONS = Object.entries(
  TIMED_TASK_STATUS_CONFIG
).map(([value, { label }]) => ({
  value: Number(value),
  label,
}));

export const getTimedTaskStatusConfig = (
  status?: number
): TimedTaskStatusConfig =>
  status === undefined
    ? DEFAULT_TIMED_TASK_STATUS_CONFIG
    : TIMED_TASK_STATUS_CONFIG[status as TimedTaskStatus] ||
      DEFAULT_TIMED_TASK_STATUS_CONFIG;

export const getTimedTaskStatusText = (status?: number) =>
  getTimedTaskStatusConfig(status).label;

export const canEditTimedTaskByStatus = (status?: number) =>
  getTimedTaskStatusConfig(status).editable;
